import { useQuery } from '@tanstack/react-query';
import { createApiCall } from 'services';

interface ShopifyCustomersParams {
  limit?: number;
  offset?: number;
}

interface ShopifyCustomer {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  created_at: Date;
  updated_at: Date;
  // Add integration metadata for display
  integration?: {
    name: string;
    logo: string;
  };
}

interface ShopifyCustomersResponse {
  customers: ShopifyCustomer[];
  total: number;
  has_more: boolean;
  next_cursor?: string;
}

export const useShopifyCustomers = (params: ShopifyCustomersParams = {}) => {
  const fetchShopifyCustomers = async (queryParams: ShopifyCustomersParams): Promise<ShopifyCustomersResponse> => {
    const { data } = await createApiCall({
      method: 'GET',
      url: '/integrations/shopify/customers',
      params: {
        limit: queryParams.limit || 50,
        offset: queryParams.offset || 0,
      },
    });

    // Transform the response to match the expected format for AddOwner modal
    const transformedCustomers = data.customers.map((customer: any) => ({
      ...customer,
      // Add name field by combining first_name and last_name for display
      name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email,
      // Add integration metadata for UI display
      integration: {
        name: 'Shopify',
        logo: 'https://cdn.iconscout.com/icon/free/png-256/free-shopify-logo-icon-download-in-svg-png-gif-file-formats--online-shopping-brand-logos-pack-icons-226579.png?f=webp&w=256',
      },
    }));

    return {
      customers: transformedCustomers,
      total: data.total,
      has_more: data.has_more,
      next_cursor: data.next_cursor,
    };
  };

  return useQuery({
    queryKey: ['shopify', 'customers', params],
    queryFn: () => fetchShopifyCustomers(params),
    staleTime: 30000, // 30 seconds
    enabled: true, // Always enabled, will be controlled by the modal open state
  });
};